import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { usePageTitle } from '../hooks/usePageTitle';
import { PlusIcon, CalendarIcon, FunnelIcon } from '@heroicons/react/24/outline';
import {
  Event,
  EventType,
  EventResponseSummary,
  getEventTypeLabel,
} from '../types/event';
import { eventsAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { ATTENDANCE_UPDATED_EVENT } from '../contexts/PushNotificationContext';
import { isStaff } from '../types/auth';
import EventCard from '../components/events/EventCard';
import EventDetailModal from '../components/events/EventDetailModal';
import MatchDetailView from '../components/match/MatchDetailView';
import EventForm from '../components/events/EventForm';
import DeleteConfirmModal from '../components/ui/DeleteConfirmModal';
import AgeGroupSelect from '../components/common/AgeGroupSelect';
import FilterAccordion from '../components/ui/FilterAccordion';
import HelpHint from '../components/help/HelpHint';

interface EventWithSummary {
  event: Event;
  summary?: EventResponseSummary;
}

const Events: React.FC = () => {
  const { user } = useAuth();
  usePageTitle('Események');
  const [searchParams] = useSearchParams();
  const { id: eventIdFromPath } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [events, setEvents] = useState<EventWithSummary[]>([]);
  const [filteredEvents, setFilteredEvents] = useState<EventWithSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [selectedAgeGroupId, setSelectedAgeGroupId] = useState<number | null>(null);
  const [selectedType, setSelectedType] = useState<string>('');
  const [showPast, setShowPast] = useState(false);

  // Modals
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [focusFeedback, setFocusFeedback] = useState(false);
  const [isMatchDetailOpen, setIsMatchDetailOpen] = useState(false);
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const loadEvents = useCallback(async () => {
    try {
      setLoading(true);
      const eventsData = await eventsAPI.getAll();

      // Load summaries for all events in parallel
      const eventsWithSummaries = await Promise.all(
        eventsData.map(async (event) => {
          try {
            const summary = await eventsAPI.getResponseSummary(event.id);
            return { event, summary };
          } catch {
            return { event };
          }
        })
      );

      setEvents(eventsWithSummaries);
    } catch (err) {
      setError('Hiba történt az események betöltésekor');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  const filterEvents = useCallback(() => {
    let filtered = [...events];
    const now = new Date();

    // Filter by past/upcoming
    if (!showPast) {
      filtered = filtered.filter((e) => new Date(e.event.endTime) >= now);
    }

    // Filter by age group
    if (selectedAgeGroupId !== null) {
      filtered = filtered.filter((e) => e.event.ageGroupId === selectedAgeGroupId);
    }

    // Filter by type
    if (selectedType !== '') {
      filtered = filtered.filter((e) => e.event.type === parseInt(selectedType));
    }

    // Sort by start time
    filtered.sort((a, b) => new Date(a.event.startTime).getTime() - new Date(b.event.startTime).getTime());

    setFilteredEvents(filtered);
  }, [events, selectedAgeGroupId, selectedType, showPast]);

  useEffect(() => {
    filterEvents();
  }, [filterEvents]);

  // Ha az értesítés gombjáról érkezett válasz, a lista újratöltődik
  useEffect(() => {
    const onAttendanceUpdated = () => {
      loadEvents();
    };

    window.addEventListener(ATTENDANCE_UPDATED_EVENT, onAttendanceUpdated);
    return () => window.removeEventListener(ATTENDANCE_UPDATED_EVENT, onAttendanceUpdated);
  }, [loadEvents]);

  // Mélylinkről érkezve (/events/12) rögtön az adott esemény nyílik meg. A régi,
  // query paraméteres alak (/events?event=12) még él, mert a korábban kiküldött
  // push értesítések azt tartalmazzák - újat már nem gyártunk belőle.
  useEffect(() => {
    const legacyParam = searchParams.get('event');
    const requestedId = eventIdFromPath ?? legacyParam;
    if (!requestedId || events.length === 0) {
      return;
    }

    const target = events.find((item) => item.event.id === parseInt(requestedId, 10));
    if (target) {
      setSelectedEvent(target.event);
      setIsDetailModalOpen(true);
      // Az edzés utáni értesítésből érkezve a visszajelzéshez görgetünk
      setFocusFeedback(searchParams.get('feedback') === '1');
    }

    // A régi query paraméteres alakot a mai /events/:id címre írjuk át; a nyitott
    // esemény címe megmarad, így a link megosztható és frissítés után is ott van
    if (legacyParam) {
      navigate(`/events/${requestedId}`, { replace: true });
    } else if (!target) {
      navigate('/events', { replace: true });
    }
  }, [events, eventIdFromPath, searchParams, navigate]);

  // A modalban írt/törölt hozzászólások után a kártya számlálója
  // újratöltés nélkül frissül
  const handleCommentCountChange = useCallback((eventId: number, count: number) => {
    setEvents((prev) =>
      prev.map((item) =>
        item.event.id === eventId
          ? { ...item, event: { ...item.event, commentCount: count } }
          : item,
      ),
    );
  }, []);

  const handleEventClick = (event: Event) => {
    setSelectedEvent(event);
    setIsDetailModalOpen(true);
    navigate(`/events/${event.id}`, { replace: true });
  };

  /** Az esemény ablakának bezárása: a cím visszaáll a listára. */
  const closeDetail = () => {
    setIsDetailModalOpen(false);
    setSelectedEvent(null);
    setFocusFeedback(false);
    navigate('/events', { replace: true });
  };

  const handleCreateEvent = () => {
    setSelectedEvent(null);
    setIsFormModalOpen(true);
  };

  const handleEditEvent = () => {
    setIsDetailModalOpen(false);
    setIsFormModalOpen(true);
  };

  const handleDeleteEvent = () => {
    setIsDetailModalOpen(false);
    setIsDeleteModalOpen(true);
  };

  const handleEventSaved = () => {
    setIsFormModalOpen(false);
    setSelectedEvent(null);
    loadEvents();
  };

  const handleEventDeleted = async () => {
    if (selectedEvent) {
      try {
        await eventsAPI.delete(selectedEvent.id);
        setIsDeleteModalOpen(false);
        setSelectedEvent(null);
        loadEvents();
      } catch (err) {
        console.error('Error deleting event:', err);
      }
    }
  };

  const canCreateEvents = isStaff(user);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Események</h1>
          <p className="hidden sm:block text-gray-600 mt-1">Események kezelése és visszajelzések</p>
        </div>
        {canCreateEvents && (
          <button
            onClick={handleCreateEvent}
            className="w-full sm:w-auto bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 flex items-center justify-center gap-2"
          >
            <PlusIcon className="h-5 w-5" />
            Új esemény
          </button>
        )}
      </div>

      {/* Filters - only for staff */}
      {isStaff(user) && (
        <div className="bg-white px-4 py-2 md:p-4 rounded-lg shadow border">
          <FilterAccordion
            activeCount={
              (selectedAgeGroupId !== null ? 1 : 0) +
              (selectedType !== '' ? 1 : 0) +
              (showPast ? 1 : 0)
            }
          >
            <div className="hidden md:flex items-center gap-2 mb-4">
              <FunnelIcon className="h-5 w-5 text-gray-500" />
              <span className="text-sm font-medium text-gray-700">Szűrők</span>
              <HelpHint id="events.filters" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 pt-2 pb-2 md:pt-0 md:pb-0">
            <div>
              <label htmlFor="ageGroup" className="block text-sm font-medium text-gray-700 mb-1">
                Korosztály
              </label>
              <AgeGroupSelect
                value={selectedAgeGroupId}
                onChange={(id) => setSelectedAgeGroupId(id)}
                autoSelectSingle={false}
                placeholder="Összes korosztály"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label htmlFor="eventType" className="block text-sm font-medium text-gray-700 mb-1">
                Típus
              </label>
              <select
                id="eventType"
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">Összes típus</option>
                <option value={EventType.Training}>{getEventTypeLabel(EventType.Training)}</option>
                <option value={EventType.Match}>{getEventTypeLabel(EventType.Match)}</option>
                <option value={EventType.Meeting}>{getEventTypeLabel(EventType.Meeting)}</option>
                <option value={EventType.Other}>{getEventTypeLabel(EventType.Other)}</option>
              </select>
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={showPast}
                  onChange={(e) => setShowPast(e.target.checked)}
                  className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                />
                <span className="text-sm font-medium text-gray-700">
                  Múltbeli események mutatása
                </span>
              </label>
            </div>
            </div>
          </FilterAccordion>
        </div>
      )}

      {/* Events Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 md:gap-6">
        {filteredEvents.map(({ event, summary }) => (
          <EventCard
            key={event.id}
            event={event}
            responseSummary={summary}
            onClick={() => handleEventClick(event)}
          />
        ))}
      </div>

      {filteredEvents.length === 0 && (
        <div className="text-center py-12">
          <CalendarIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Nincsenek események</h3>
          <p className="mt-1 text-sm text-gray-500">
            {isStaff(user) && (selectedAgeGroupId !== null || selectedType)
              ? 'A szűrési feltételeknek megfelelő esemény nem található.'
              : showPast
              ? 'Még nem került fel esemény a rendszerbe.'
              : 'Nincs közelgő esemény.'}
          </p>
        </div>
      )}

      {/* Modals */}
      <EventDetailModal
        event={selectedEvent}
        isOpen={isDetailModalOpen}
        onClose={closeDetail}
        focusFeedback={focusFeedback}
        onEdit={handleEditEvent}
        onDelete={handleDeleteEvent}
        onResponseChange={loadEvents}
        onCommentCountChange={handleCommentCountChange}
        onOpenMatch={selectedEvent?.type === EventType.Match ? () => {
          setIsDetailModalOpen(false);
          setIsMatchDetailOpen(true);
        } : undefined}
      />

      <MatchDetailView
        event={selectedEvent}
        isOpen={isMatchDetailOpen}
        onClose={() => {
          // A mérkőzés ablakából vissza az esemény ablakához (nem a listára)
          setIsMatchDetailOpen(false);
          if (selectedEvent) setIsDetailModalOpen(true);
        }}
        onEditEvent={selectedEvent ? handleEditEvent : undefined}
      />

      <EventForm
        isOpen={isFormModalOpen}
        onClose={() => {
          setIsFormModalOpen(false);
          setSelectedEvent(null);
        }}
        event={selectedEvent}
        onSave={handleEventSaved}
      />

      <DeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setSelectedEvent(null);
        }}
        onConfirm={handleEventDeleted}
        title="Esemény törlése"
        message={`Biztosan törölni szeretnéd a következő eseményt: ${selectedEvent?.title}?`}
        itemName={selectedEvent?.title || ''}
      />
    </div>
  );
};

export default Events;
